/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2014-2018, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * A service which handles authentication and authorization for the current user.
 */
angular.module('bulbApp.authModule')
  .service('BulbAuthService', ['$injector', '$log', '$q', '$rootScope', '$state', '$timeout', '$window', 'google', 'moment',
    'Restangular', 'AnalyticsService', 'BulbAjaxService', 'BulbFetchUserService',
    'BulbModalService', 'BulbErrorService', 'AUTH', 'CONFIG', BulbAuthService]);

function BulbAuthService(
  $injector,
  $log,
  $q,
  $rootScope,
  $state,
  $timeout,
  $window,
  google,
  moment,
  Restangular,
  AnalyticsService,
  BulbAjaxService,
  BulbFetchUserService,
  BulbModalService,
  BulbErrorService,
  AUTH,
  CONFIG,
) {
  // Fetching the current user from our fetch-user service. The service makes the backend call during Angular's
  // Bootstrap process.
  const ORIGINAL_USER = BulbFetchUserService.originalUser;
  const that = this;

  /**
   * The current user, either authenticated or anonymous.
   */
  // DO LATER : since we are now fetching the entire account, rather than just auth info, this should probably be
  // renamed from currentUser to currentAccount... or even just account.  It also matches up better with the server
  // since we don't really have user entities.
  this.currentUser = {
    roles: [AUTH.ROLES.USER],
    fetched: false,

    isAuthenticated: function isAuthenticated() {
      return this.hasRole(AUTH.ROLES.REGISTERED_USER);
    },

    hasRole: function hasRole(authorizedRoles) {
      if (!Array.isArray(authorizedRoles)) {
        authorizedRoles = [authorizedRoles];
      }
      return this.roles.filter((output) => authorizedRoles.includes(output)).length > 0;
    },

    canCreate: function canCreate(entityType) {
      if (entityType === 'Group' && this.isAuthenticated()) {
        if (this.features.GroupFeature.maxGroups < 0) {
          return true;
        }
        return this.features.GroupFeature.maxGroups > this.features.GroupFeature.currentGroups;
      }
      return this.isAuthenticated();
    },

    hasPermission: function hasPermission(entity, permission) {
      return entity.permissions.indexOf(permission) !== -1;
    },

    isPrivate: function isPrivate() {
      return this.features.PrivateProfileFeature.enabled;
    },
  };

  /**
   * Set the user for the current session.
   * @param user User that is sent back from the server.
   */
  this.setCurrentUser = function (user) {
    if (user) {
      // FUTURE : a deep 'extend' to merge properties, yet smart enough to also remove properties that don't
      // exist in user, would be nice here.  Unfortunately, 'extend' does not recurse to children, and
      // lodash's assign/merge, which is recursive, but it won't remove a property from the destination which
      // doesn't exist in the source.
      angular.extend(this.currentUser, user);
      broadcast({
        event: 'verifyAccount',
        accountId: user.id,
        accountVersion: user.version,
      });

      // Has the terms of use and privacy policy changed since this user last accepted them?
      // NOTE : make sure terms acceptance dialog isn't shown for masqueraded admins.
      if (!this.currentUser.acceptedLatestTerms && !this.currentUser.hasRole(AUTH.ROLES.SWITCHED_USER)) {
        $timeout(() => {
          // Show the updated terms dialog:
          BulbModalService.open({
            titleCode: '',
            templateUrl: 'ajs/components/auth/termsupdated-modal.html',
            windowClass: 'bulb-onboarding-modal-with-close scrollable',
            closeOnClick: true,
            showOkButton: false,
            showCancelButton: false,
          });

          // Update the account has having accepted the updated terms:
          Restangular.one('accounts', that.currentUser.id).patch({
            acceptedLatestTerms: true,
          }).catch((error) => {
            $log.error(`failed to save acceptance of latest terms ${error}`);
          });

          that.currentUser.acceptedLatestTerms = true;
        });
      }

      // Logic to handle the offboarding for the seniors.
      // We only ever display the offboarding modal / reminder in the following instances:
      // a) If the senior *hasn't* added the new, alternate email.
      // b) If the user hit the 'remind me later' button, we calculate a week ahead from the instance they hit the
      //    remind me later button.
      // If point (b) holds true, we then check to see if it is after school hours -- as to not display the modal
      // during school hours.
      const currentOffBoardingPrefs = this.currentUser.prefs.offBoarding;
      if (currentOffBoardingPrefs !== null && !currentOffBoardingPrefs.isEmailAdded
        && isTimeToShowOffBoardingReminder(currentOffBoardingPrefs.remindMeLaterDate)
        && !this.currentUser.hasRole(AUTH.ROLES.SWITCHED_USER)) {
        // Close previously opened modal before opening new.
        BulbModalService.dismissAllModals();

        // Show the offboarding modal for the seniors
        const modalOptions = {
          headerTemplateUrl: 'ajs/components/offboarding/offboarding-fontawesome.html',
          titleCode: 'off_board_modal_header',
          template: '<bulb-offboarding></bulb-offboarding>',
          windowClass: 'updated-modal-design bulb-offboarding-alert-modal',
          closeOnClick: true,
          showOkButton: false,
          showCancelButton: false,
        };
        BulbModalService.open(modalOptions);
      }

      /**
       * Show trial expired modal if showHPTrialExpiredModal value is true
       * To prevent modal from re-rendering again we set the showHPTrialExpiredModal value to false.
       */
      if (!this.currentUser.hasRole(AUTH.ROLES.SWITCHED_USER) && this.currentUser.prefs.showHPTrialExpiredModal) {
        // Close previously opened modal before opening new.
        BulbModalService.dismissAllModals();

        BulbModalService.show({
          titleCode: 'account_hp_trial_ended_title',
          bodyCode: 'account_hp_trial_ended_body',
          okButtonCode: 'explore_get_it',
          cancelButtonCode: 'account_hp_trial_ended_request_info',
          headerTemplateUrl: 'ajs/components/auth/hp-trialend-modal-header.html',
          windowClass: 'updated-modal-design bulb-aero-modal',
        }).then(() => {
          that.planlistState();
        }, (action) => {
          if (action === 'cancel') {
            $window.open(`https://${$rootScope.HOST_NAME}/h/contact-request-a-demo/`, '_blank');
          }
        });

        // call the accounts api to update showHPTrialExpiredModal value to false.
        // Using $timeout here to allow Restangular configuration to initialize before calling
        // the rest api because there can be a race condition the first time this method is called
        // which will result in the Restangular api base url missing the /b/rest, causing url to
        // be invalid.
        this.currentUser.prefs.showHPTrialExpiredModal = false;
        $timeout(() => {
          Restangular.one('accounts', that.currentUser.id).patch({ prefs: that.currentUser.prefs })
            .then((updatedAccount) => {
              that.setCurrentUser(updatedAccount);
            }).catch((error) => {
              $log.error(`failed to update showHPTrialExpiredModal ${error}`);
            });
        });
      }
    } else {
      // DO LATER : since we now use an entire account record, this isn't sufficient, as it only overwrites a few
      // values, but leaves the rest there.
      // Currently, this isn't a problem because we do a full reload when the user signs in or out.
      this.currentUser.id = null;
      this.currentUser.displayName = AUTH.ANONYMOUS_ACCOUNT.DISPLAY_NAME;
      this.currentUser.firstName = AUTH.ANONYMOUS_ACCOUNT.FIRSTNAME;
      this.currentUser.lastName = AUTH.ANONYMOUS_ACCOUNT.LASTNAME;
      this.currentUser.username = AUTH.ANONYMOUS_ACCOUNT.USERNAME;
      this.currentUser.roles = [AUTH.ROLES.USER];
    }

    // Update user data for analytics:
    // loggedInStatus is set per user, and is only set once (per browser).  We are interested in if the user has
    // ever logged in, not whether they are currently logged in.  So once a user has logged in, we can set it,
    // and we never set it back to not logged in.  Based on how the analytics works for 'user' custom diminsions,
    // It will stay set unless the user clears the analytics cookies.
    if (this.currentUser.isAuthenticated()) {
      AnalyticsService.pushData({
        loggedInStatus: 'logged in',
      });
    }
  };

  /**
   * Function that determines whether or not to show the offboarding modal / reminder.
   * @param remindMeLaterTimeStamp The date that we should remind the user if they clicked the 'remind me later' button.
   * @returns {boolean} Whether or not to show the offboarding modal / reminder.
   */
  function isTimeToShowOffBoardingReminder(remindMeLaterTimeStamp) {
    // Grab the current date in UTC format:
    const currentDateTimeStamp = moment.utc(Date.now());
    // Can add this check back in if it is desired to restrict this modal to after hours, but removing for now.
    // (new Date().getHours() > 16)
    return ((currentDateTimeStamp > remindMeLaterTimeStamp));
  }

  this.setCurrentUser(ORIGINAL_USER);

  this.signIn = function (credentials) {
    // This imitates a standard form POST to work with spring security.  After login succeeds, the response is
    // redirected to /b/rest/auth/current to fetch the current account.  We need to use the normal spring
    // security login flow rather than our own authentication controller handler to make sure we take advantage of
    // authentication protection schemes such as session fixation protection.

    // format the credentials as standard POST form data, rather than a json body:
    const formData = jQuery.param(credentials);
    return Restangular.one('auth', 'authenticate').customPOST(
      formData,
      '',
      {
        client_name: (!credentials.multiFactorCode ? 'UsernamePasswordClient' // jshint ignore:line
          : 'UsernamePasswordMultiFactorClient'),
        targetUrl: '/b/rest/auth/current',
      },
      { 'Content-Type': 'application/x-www-form-urlencoded' },
    ).then((result) => {
      // Let other bulb browser windows/tabs know there has been a signIn.
      broadcast({ event: 'signIn' });
      return result;
    });
  };

  this.authenticateViaGoogle = function () {
    const deferred = $q.defer();

    // We are binding the callback so we have access to 'deferred' within the callback.
    const callback = authenticateWithGoogleCallback.bind(that, deferred);

    google.auth2.authorize({
      client_id: CONFIG.google.client.id, // jshint ignore:line
      scope: 'email profile',
      response_type: 'code', // jshint ignore:line
      prompt: 'select_account',
      cookie_policy: 'single_host_origin', // jshint ignore:line
    }, callback);

    return deferred.promise;
  };

  function authenticateWithGoogleCallback(deferred, authorizeResponse) {
    if (authorizeResponse.error) {
      switch (authorizeResponse.error) {
        case 'access_denied':
          BulbModalService.show({
            titleCode: 'sign_in_google_access_denied_title',
            templateUrl: 'ajs/components/auth/auth-googleaccessdenied.html',
            showCancelButton: false,
          }).finally(() => {
            deferred.reject('google_authentication_failed');
          });
          break;
        case 'idpiframe_initialization_failed':
          BulbErrorService.showError('authorizeResponse.details');
          deferred.reject('google_authentication_failed');
          break;
        case 'popup_closed_by_user':
          deferred.reject('google_authentication_failed');
          break;
        default:
          deferred.reject('google_authentication_failed');
      }
    } else {
      const params = {
        client_name: 'GoogleTokenClient', // jshint ignore:line
        targetUrl: '/b/rest/auth/current',
        code: authorizeResponse.code,
      };
      Restangular.one('auth', 'authenticate').customGET('', params).then((response) => {
        // Let other bulb browser windows/tabs know there has been a signIn.
        broadcast({ event: 'signIn' });
        deferred.resolve(response);
      }, (error) => {
        // Google signin worked, but bulb authenticate via google failed.  Probably due to not having
        // a bulb account attached to the google account.
        deferred.reject(error);
      });
    }
  }

  this.signOut = function (url, disableRedirect) {
    Restangular.one('auth', 'logout').customPOST().then(() => {
      // FUTURE : we currently do a full reload so that we can refresh permissions on all data for the
      // successful sign in.  In the future, it would be nice to clean/refresh the client state without the
      // reload, but this is the easiest way to handle it for now.
      // FUTURE : eventually, it would be nice to simply clean/refresh the client state when the user logs out,
      // however, this is the easiest solution for now, and is necessary at least until we move past the hybrid
      // phase.

      // Let other bulb browser windows/tabs know there has been a signOut.
      broadcast({ event: 'signOut' });
      localStorage.removeItem('googleModalShown');

      if (!disableRedirect) {
        // Reload the app on the landing page following a log out if no redirect url is provided:
        if (!url) {
          $window.location.href = '/';
        } else {
          $window.location.href = url;
        }
      }
    });
  };

  this.isStateAuthorized = function (state) {
    let isAuthorized = false;
    if (state.bulbAuth) {
      if (state.bulbAuth.authorizedRoles) {
        isAuthorized = this.currentUser.hasRole(state.bulbAuth.authorizedRoles);
      }
      if (state.bulbAuth.isAuthorized) {
        isAuthorized = isAuthorized || $injector.invoke(state.bulbAuth.isAuthorized);
      }
    } else {
      isAuthorized = true;
    }
    return isAuthorized;
  };

  // initialize the currentUser based on the server session
  this.fetchCurrentUser = function () {
    return BulbAjaxService.getCurrentAccount().then((account) => {
      that.setCurrentUser(account.plain());
      that.currentUser.fetched = true;
      return that.currentUser;
    });
  };

  /**
   * Allow an admin to sign in as another user
   *
   * @param username The username of the user to sign in as.
   */
  this.adminSignInAs = function (username) {
    const params = {
      action: 'switchUser',
      username,
    };
    if (this.currentUser.hasRole(AUTH.ROLES.ADMIN)) {
      params.client_name = 'BulbAdminSwitchUserClient'; // jshint ignore:line
    } else if (this.currentUser.hasRole(AUTH.ROLES.ORGANIZATION_ADMIN)) {
      params.client_name = 'OrgAdminSwitchUserClient'; // jshint ignore:line
    }

    Restangular.one('auth', 'authenticate').customGET('', params).then(() => {
      // FUTURE : we currently do a full reload so that we can refresh permissions on all data for the
      // successful sign in.  In the future, it would be nice to clean/refresh the client state without the
      // reload, but this is the easiest way to handle it for now.
      // FUTURE : eventually, it would be nice to simply clean/refresh the client state when the user logs out,
      // however, this is the easiest solution for now, and is necessary at least until we move past the hybrid
      // phase.
      // FUTURE : If/when we are able to do a client refresh rather than reload, that refresh needs to
      // update the user id/username for metrics purposes as well.  See BulbMetricsService.

      // Let other bulb browser windows/tabs know there has been an adminSignInAs.
      broadcast({ event: 'adminSignInAs' });

      // Reload the app on the profile page of the signed in user:
      $window.location.href = `/${username}`;
    }, () => {
      // Items that appear within the admin dashboard are not translated according to standards
      const modalOptions = {
        titleCode: 'Unable to switch users',
        bodyText: 'Cannot sign in to account. The account cannot be found or you cannot login as another admin.',
        showOkButton: true,
        showCancelButton: false,
        fontAwesomeCode: 'fas fa-exclamation-triangle',
        windowClass: 'updated-modal-design',
      };

      BulbModalService.show(modalOptions);
    });
  };

  /*
  * Allow and admin to enable/disable accounts by username
    * @param user id
    * @param enabled
   */
  this.setUserEnabled = function (id, enabled) {
    if (that.currentUser.hasRole(AUTH.ROLES.ADMIN)) {
      enabled = !enabled;
      return Restangular.one('admin/accounts', id)
        .customPOST({ enabled }, 'enabled');
    }
    return new Error('Unable to toggle user status at BulbAuthService.setUserEnabled.');
  };

  /**
   * Function to exit the currently 'signed in as' user and return to the admin account.
   */
  this.adminExitUser = function () {
    const params = {
      action: 'switchBack',
      // NOTE: Both clients (BulbAdmin/OrgAdmin) have the ability to switch back, but we don't need to determine
      // which client to use as we did above. So it is just set to BulbAdmin here.
      client_name: 'BulbAdminSwitchUserClient', // jshint ignore:line
    };
    Restangular.one('auth', 'authenticate').customGET('', params).then(() => {
      // FUTURE : we currently do a full reload so that we can refresh permissions on all data for the
      // successful sign in.  In the future, it would be nice to clean/refresh the client state without the
      // reload, but this is the easiest way to handle it for now.
      // FUTURE : eventually, it would be nice to simply clean/refresh the client state when the user logs out,
      // however, this is the easiest solution for now, and is necessary at least until we move past the hybrid
      // phase.
      // FUTURE : If/when we are able to do a client refresh rather than reload, that refresh needs to
      // update the user id/username for metrics purposes as well.  See BulbMetricsService.

      // We need to re-fetch the currentUser to get the original user that was there from before the switch,
      // and then we determine which state to properly send the user to.
      that.fetchCurrentUser().then((fetchedUser) => {
        that.setCurrentUser(fetchedUser);

        // Let other bulb browser windows/tabs know there has been a signOut.
        broadcast({ event: 'adminExitUser' });

        // This is a bulb admin, so redirect them to the admin dashboard.
        if (that.currentUser.hasRole(AUTH.ROLES.ADMIN)) {
          const urlReturnToDashboard = localStorage.getItem('urlReturnToDashboard');
          if (urlReturnToDashboard.indexOf('organizations') !== -1) {
            $state.go('organization', { id: localStorage.getItem('organizationId') });
          } else {
            $window.location.href = `/b/${urlReturnToDashboard}`;
          }
        } else if (that.currentUser.hasRole(AUTH.ROLES.ORGANIZATION_ADMIN)) {
          const { groupMemberships } = that.currentUser;
          let returningOrgId = '';

          for (let i = 0; i < groupMemberships.length; i++) {
            if (groupMemberships[i].isAdmin && groupMemberships[i].type === 'ORGANIZATION') {
              returningOrgId = groupMemberships[i].groupId;
              break;
            }
          }

          $state.go('organization', { id: returningOrgId });
        }
      });
    });
  };

  /**
   * It will redirect to planlist page if stripe is enabled for user.
   */
  this.planlistState = function () {
    if (CONFIG.stripe.enabled) {
      $state.go('planlist');
    } else {
      BulbModalService.show({
        titleCode: 'server_error_title',
        bodyCode: 'planlist_error_body',
        showCancelButton: false,
      });
    }
  };

  /**
   * Dictate whether the current user has the ability to make MacMillan groups. We need to check for MACMILLAN_BOOKS_1
   * as MACMILLAN_BOOKS_2 and onward will automatically copy the collection and will not show the MacMillan group
   * dialogue
   *
   * @param codes The codes associated with the user.
   */
  this.isAbleToMakeMacMillanGroups = function (codes) {
    if (codes) {
      for (const code in codes) {
        if (codes.hasOwnProperty(code)) {
          if (codes[code].type.includes('MACMILLAN_BOOKS_1')) {
            return true;
          }
        }
      }
    }
  };

  // register event listeners
  $rootScope.$on('$stateChangeStart', (event, nextState, fromState) => {
    if ((nextState.name === 'signIn' || nextState.name === 'signUp' || nextState.name === 'trial60'
      || nextState.name === 'trial180') && that.currentUser.isAuthenticated()) {
      /**
       * Handle case where user is already logged in and try to hit the organization signup url.
       */
      if (fromState.orgToken && nextState.name === 'signUp') {
        event.preventDefault();
        BulbModalService.show({
          titleCode: 'organization_already_loggedin_error_title',
          fontAwesomeCode: 'fas fa-random',
          okButtonCode: 'logout',
          windowClass: 'updated-modal-design bulb-aero-modal bulb-organization-modal',
          bodyCode: 'organization_already_loggedin_error_body',
        }).then(() => {
          that.signOut();
        }).finally(() => {
          // Send the user back to the same page, in case of cancel.
          $window.history.back();
        });
      } else {
        // sending an already logged in user to their profile, rather than going to the sign in or trybulb page
        event.preventDefault();
        $state.go('author', { username: that.currentUser.username });
      }
    } else if (nextState.name === 'buyBulb' && that.currentUser.isAuthenticated()) {
      event.preventDefault();
      if (that.currentUser.type === 'FREE') {
        // Send logged-in free user to account options.
        $state.go('account.options');
      } else {
        // Send logged-in subscribed user to account settings.
        $state.go('account.settings');
      }
    } else if (nextState.name === 'trialEnding') {
      event.preventDefault();
      if (that.currentUser.isAuthenticated()) {
        // Send logged-in user to account billing.
        $state.go('account.billing');
      } else {
        // Send Anonymous user to upgrading-to-bulb.
        $state.go('unit.page', { unitPath: 'upgrading-to-bulb' });
      }
    } else if (!that.isStateAuthorized(nextState)) {
      event.preventDefault();
      $state.go('accessDenied', {}, { location: false });
    }

    // Check to add urls on which the user should return while clicking on "return to dashboard".
    if (nextState.name && (nextState.name === 'organizations_admin' || nextState.name === 'admin')) {
      localStorage.setItem('urlReturnToDashboard', nextState.name.split('_')[0]);
    }

    // If logged in user has BULB PLUS account, then trying to access planlist will redirect them to
    // account settings page.
    if (nextState.name === 'planlist' && that.currentUser.isAuthenticated() && that.currentUser.featureSet === 'PLUS') {
      event.preventDefault();
      $state.go('account.settings');
    }

    // If logged in user is from Europe, then trying to access planlist will open a
    // service modal.
    if (nextState.name === 'planlist' && that.currentUser.isAuthenticated() && !CONFIG.stripe.enabled) {
      event.preventDefault();
      that.planlistState();
    }
  });

  /**
   * Broadcast a message to other instances of bulb running in other browser windows/tabs.
   * @param message Message to broadcast.
   */
  function broadcast(message) {
    try {
      // Using local storage to implement the message sending and notifications.
      localStorage.setItem('authMessage', JSON.stringify(message));
      localStorage.removeItem('authMessage');
    } catch (error) {
      $state.go('blockingAccess', { error: error.message });
    }
  }

  /**
   * Handle a message from another instance of bulb running in another browser window/tab.
   * @param event Event containing the message information.
   */
  function messageReceive(event) {
    // Using local storage to implement the message sending and notifications.
    // Ignore any events that aren't auth message broadcasts
    if (event.key === 'authMessage' && event.newValue !== null) {
      const contents = JSON.parse(event.newValue);
      switch (contents.event) {
        case 'signIn':
        case 'signOut':
        case 'adminSignInAs':
        case 'adminExitUser':
          // If there was a signIn or signOut executed in another browser window, reload this one as well so
          // that the login information matches
          // FUTURE: when we make the necessary modifications to not have to reload when a signIn/signOut
          // happens, this implementation can be changed to handle the message more gracefully.
          $window.location.reload();
          break;
        case 'verifyAccount':
          // If we are doing an update, we only want to reload if the account has changed, otherwise we
          // simply update the current user details if the version has changed.  Otherwise do nothing.
          if (contents.accountId !== that.currentUser.id) {
            $window.location.reload();
          } else if (contents.accountVersion !== that.currentUser.version) {
            that.fetchCurrentUser();
          }
          break;
      }
    }
  }

  // Register messageReceive to handle localStorage events:
  $window.addEventListener('storage', messageReceive);
}
